import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { HeaderBox } from './mBookListPage';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DivineQuotesService from '../../Services/DivineQuotes';
import { useDownloadApp } from '../../Contexts/DownloadAppContext';
import '../../Styles/DivineQuote.css';

/** Fixed 2-column Pinterest-style masonry for mobile Amrit Vachan */
const COLUMN_COUNT = 2;

const MobileDivineQuotes = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setShowDownloadPopup } = useDownloadApp();
  const [divineQuotes, setDivineQuotes] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const FIXED_BOTTOM_BAR_HEIGHT = 56;
  const PAGE_SIZE = 30;
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const divineQuotesLengthRef = useRef(0);
  const loadingMoreRef = useRef(false);
  divineQuotesLengthRef.current = divineQuotes.length;

  // Split quotes into two masonry columns (Pinterest-style staggered layout)
  const masonryColumns = useMemo(
    () =>
      Array.from({ length: COLUMN_COUNT }, (_, columnIndex) =>
        divineQuotes.filter((_, index) => index % COLUMN_COUNT === columnIndex)
      ),
    [divineQuotes]
  );

  useEffect(() => {
    const fetchData = async () => {
      const logdetails: string[] = [];
      logdetails.push('fetchDivineQuotes start');
      setLoading(true);

      try {
        const response = await DivineQuotesService.getDivineQuotes('', 0, PAGE_SIZE);
        const items = response.result?.items || [];
        const total = response.result?.totalRecords || 0;

        setDivineQuotes(items);
        setTotalRecords(total);
        setHasMore(items.length < total);
        logdetails.push(`fetchDivineQuotes success count=${items.length} totalRecords=${total}`);
      } catch (error) {
        console.error('Error fetching divine quotes:', error);
        logdetails.push(`fetchDivineQuotes error=${String(error)}`);
      } finally {
        setLoading(false);
        console.log('[MobileDivineQuotes]', logdetails.join(' | '));
      }
    };

    fetchData();
  }, []);

  /** Build today's date string to match API quote date format (DD/MM/YYYY) */
  const getTodayDateStr = () => {
    const today = new Date();
    return `${String(today.getDate()).padStart(2, '0')}/${String(
      today.getMonth() + 1
    ).padStart(2, '0')}/${today.getFullYear()}`;
  };

  const handleQuoteClick = (quote: any, absoluteIndex: number) => {
    const logdetails: string[] = [];
    logdetails.push(`quoteClick id=${quote?.id} index=${absoluteIndex}`);
    logdetails.push('action=showDownloadPopup');
    console.log('[MobileDivineQuotes]', logdetails.join(' | '));
    setShowDownloadPopup(true);
  };

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const start = divineQuotesLengthRef.current;
      const response = await DivineQuotesService.getDivineQuotes('', start, PAGE_SIZE);
      const items = response.result?.items || [];
      const total = response.result?.totalRecords || 0;
      setDivineQuotes(prev => [...prev, ...items]);
      setTotalRecords(total);
      if (start + items.length >= total) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more quotes:', error);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { root: scrollRef.current, rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMore]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <HeaderBox>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ position: 'absolute', left: 16, top: 35, p: 0 }}
        >
          <ArrowBackIosIcon sx={{ color: 'white', fontSize: '18px' }} />
        </IconButton>
        <IconButton
          onClick={() => navigate('/')}
          sx={{ position: 'absolute', left: 48, top: 35, p: 0 }}
        >
          <HomeOutlinedIcon sx={{ color: 'white', fontSize: '24px' }} />
        </IconButton>
        <Typography variant="caption" sx={{ color: '#ffffffdd', opacity: 0.9, mb: 0.5 }}>
          {t('shrihari_tr')}
        </Typography>
        <Typography variant="h6" sx={{ color: '#ffffffdd', opacity: 0.9, fontSize: 15 }}>
          {t('Amrit_Vachan_tr')}
        </Typography>
      </HeaderBox>

      <Box
        sx={{
          backgroundColor: '#fff6e1',
          padding: '8px 16px',
          textAlign: 'center',
          borderBottom: '1px solid #f7ebce',
        }}
      >
        <Typography variant="body2" sx={{ color: '#555' }}>
          {t('Total_Records_tr')}: {totalRecords}
        </Typography>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          backgroundColor: '#fff6e1',
          flexGrow: 1,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          padding: '10px',
          paddingBottom: `${FIXED_BOTTOM_BAR_HEIGHT + 16}px`,
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                gap: '10px',
                width: '100%',
              }}
            >
              {masonryColumns.map((column, colIndex) => (
                <Box
                  key={colIndex}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    minWidth: 0,
                  }}
                >
                  {column.map((divquote: any, itemIndex: number) => {
                    const quoteNumber = divquote?.name?.match(/\d+/)?.[0] || '';
                    const absoluteIndex = divineQuotes.findIndex((q) => q.id === divquote.id);
                    const todayStr = getTodayDateStr();
                    const isFirstQuote = absoluteIndex === 0;
                    const isTodayQuote =
                      isFirstQuote || divquote?.quotesStrDate?.trim() === todayStr;

                    return (
                      <Box
                        key={`${colIndex}-${itemIndex}-${divquote?.id}`}
                        onClick={() => handleQuoteClick(divquote, absoluteIndex)}
                        sx={{
                          boxSizing: 'border-box',
                          borderRadius: '14px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          border: isTodayQuote ? '2px solid #f3c76d' : '1px solid #f7ebce',
                          position: 'relative',
                          background: isTodayQuote
                            ? 'linear-gradient(135deg, #fff8d6 0%, #ffd86b 20%, #fff4b0 40%, #ffcc4d 60%, #fff8d6 100%)'
                            : '#fff',
                          padding: isTodayQuote ? '3px' : 0,
                          boxShadow: isTodayQuote
                            ? '0 8px 12px rgba(243, 199, 109, 0.4)'
                            : '0 4px 8px rgba(0,0,0,0.05)',
                          cursor: 'pointer',
                        }}
                      >
                        <div className="divine-quote-thumb-wrap">
                          {isTodayQuote && (
                            <div className="divine-quote-corner-ribbon">
                              <span className="divine-quote-corner-ribbon__label">
                                {t("Today's_divinequote_tr")}
                              </span>
                            </div>
                          )}
                          <img
                            src={divquote?.quotesThumbPath}
                            alt="Divine Quote Thumbnail"
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                            }}
                          />
                        </div>

                        <Box
                          sx={{
                            padding: '10px',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {divquote?.quotesMessage && (
                            <Typography
                              sx={{
                                fontFamily: 'ChanakyaUni',
                                fontSize: '14px',
                                mb: '6px',
                                lineHeight: 1.4,
                                color: '#333',
                                textAlign: 'left',
                              }}
                            >
                              {divquote.quotesMessage}
                            </Typography>
                          )}

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                              mt: 'auto',
                            }}
                          >
                            <Box sx={{ width: '100%', height: '1px', backgroundColor: '#1a237e' }} />
                            <Typography
                              sx={{
                                fontFamily: 'ChanakyaUni',
                                fontSize: '13px',
                                m: 0,
                                color: '#1a237e',
                                textAlign: 'left',
                              }}
                            >
                              {t('Daily_Divine_Quote_No_tr')} {quoteNumber}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: '#666',
                                fontFamily: 'ChanakyaUni',
                                textAlign: 'left',
                              }}
                            >
                              {t('Date_tr')} {divquote?.quotesStrDate}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
            {hasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MobileDivineQuotes;
